import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    AlertTriangle,
    Ban,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Clock3,
    Loader2,
    MinusCircle,
    ScanSearch,
    ShieldCheck,
    XCircle,
} from "lucide-react";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import {
    fetchLatestSecretDiscoveryScan,
    fetchSecretDiscoveryScanTargets,
    formatDuration,
    formatScanTimestamp,
    SCAN_STATE_LABELS,
    SecretDiscoveryScan,
    SecretDiscoveryScanConflictError,
    SecretDiscoveryScanState,
    SecretDiscoveryScanTarget,
    SecretDiscoveryTargetState,
    startSecretDiscoveryScan,
} from "../CloudAnalytics/secretDiscoveryService";

const RUNNING_POLL_INTERVAL_MS = 4000;

export const SECRET_DISCOVERY_LATEST_SCAN_KEY = "secret-discovery-latest-scan";
export const SECRET_DISCOVERY_TARGETS_KEY = "secret-discovery-scan-targets";
export const SECRET_DISCOVERY_COUNTS_KEY = "secret-discovery-footprint-counts";

interface UseSecretDiscoveryScanArgs {
    cloudProvider: string;
    tenantName?: string;
    projectId?: string;
}

export interface SecretDiscoveryScanController {
    supported: boolean;
    latestScan: SecretDiscoveryScan | null;
    isRunning: boolean;
    isBusy: boolean;
    isLoading: boolean;
    isStarting: boolean;
    startScan: () => void;
    startError: string | null;
    clearStartError: () => void;
    targets: SecretDiscoveryScanTarget[];
    areTargetsLoading: boolean;
    targetsEnabled: boolean;
    setTargetsEnabled: (enabled: boolean) => void;
}

export function useSecretDiscoveryScan({
    cloudProvider,
    tenantName,
    projectId,
}: UseSecretDiscoveryScanArgs): SecretDiscoveryScanController {
    const queryClient = useQueryClient();
    const [startError, setStartError] = useState<string | null>(null);
    const [targetsEnabled, setTargetsEnabled] = useState(false);
    const previousStatusRef = useRef<SecretDiscoveryScanState | null>(null);

    const supported =
        String(cloudProvider || "").toLowerCase() === "gcp" && Boolean(tenantName) && Boolean(projectId);

    const { data: latestScan = null, isLoading } = useQuery(
        [SECRET_DISCOVERY_LATEST_SCAN_KEY, tenantName, projectId],
        () => fetchLatestSecretDiscoveryScan(tenantName, projectId),
        {
            enabled: supported,
            refetchOnWindowFocus: false,

            refetchInterval: (scan: SecretDiscoveryScan | null | undefined) =>
                scan?.status === "IN_PROGRESS" ? RUNNING_POLL_INTERVAL_MS : false,
        },
    );

    const isRunning = latestScan?.status === "IN_PROGRESS";


    const { data: targets = [], isLoading: areTargetsLoading } = useQuery(
        [SECRET_DISCOVERY_TARGETS_KEY, latestScan?.scanId],
        () => fetchSecretDiscoveryScanTargets(latestScan?.scanId),
        {
            enabled: supported && targetsEnabled && Boolean(latestScan?.scanId),
            refetchOnWindowFocus: false,
            refetchInterval: isRunning ? RUNNING_POLL_INTERVAL_MS : false,
        },
    );

    useEffect(() => {
        const previous = previousStatusRef.current;
        const current = latestScan?.status ?? null;
        if (previous === "IN_PROGRESS" && current && current !== "IN_PROGRESS") {
            queryClient.invalidateQueries([SECRET_DISCOVERY_COUNTS_KEY]);
            queryClient.invalidateQueries(["secret-discovery"]);
        }
        previousStatusRef.current = current;
    }, [latestScan?.status, queryClient]);

    const startMutation = useMutation(
        () => startSecretDiscoveryScan(tenantName as string, projectId as string),
        {
            onSuccess: (scan) => {
                setStartError(null);
                if (scan) {
                    queryClient.setQueryData(
                        [SECRET_DISCOVERY_LATEST_SCAN_KEY, tenantName, projectId],
                        scan,
                    );
                }
                queryClient.invalidateQueries([SECRET_DISCOVERY_LATEST_SCAN_KEY, tenantName, projectId]);
            },
            onError: (error: any) => {
                if (error instanceof SecretDiscoveryScanConflictError) {

                    setStartError(error.message);
                    queryClient.invalidateQueries([SECRET_DISCOVERY_LATEST_SCAN_KEY, tenantName, projectId]);
                    return;
                }
                setStartError(error?.message || "Unable to start the scan");
            },
        },
    );

    return {
        supported,
        latestScan,
        isRunning,
        isBusy: isRunning || startMutation.isLoading,
        isLoading,
        isStarting: startMutation.isLoading,
        startScan: () => {
            if (!supported || isRunning || startMutation.isLoading) return;
            setStartError(null);
            startMutation.mutate();
        },
        startError,
        clearStartError: () => setStartError(null),
        targets,
        areTargetsLoading,
        targetsEnabled,
        setTargetsEnabled,
    };
}

const SCAN_STATE_STYLES: Record<SecretDiscoveryScanState, { className: string; icon: typeof ShieldCheck }> = {
    IN_PROGRESS: {
        className:
            "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-300",
        icon: Loader2,
    },
    COMPLETED: {
        className:
            "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300",
        icon: ShieldCheck,
    },
    COMPLETED_WITH_ERRORS: {
        className:
            "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300",
        icon: AlertTriangle,
    },
    FAILED: {
        className:
            "border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300",
        icon: XCircle,
    },
    CANCELLED: {
        className:
            "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-300",
        icon: Ban,
    },
};

const TARGET_STATE_STYLES: Record<SecretDiscoveryTargetState, { className: string; icon: typeof ShieldCheck }> = {
    PENDING: { className: "text-muted-foreground", icon: Clock3 },
    IN_PROGRESS: { className: "text-blue-600 dark:text-blue-400", icon: Loader2 },
    COMPLETED: { className: "text-emerald-600 dark:text-emerald-400", icon: CheckCircle2 },
    FAILED: { className: "text-red-600 dark:text-red-400", icon: XCircle },
    SKIPPED: { className: "text-muted-foreground", icon: MinusCircle },
};

export function StartSecretScanButton({
    controller,
}: {
    controller: SecretDiscoveryScanController;
}) {
    if (!controller.supported) return null;

    const disabled = controller.isBusy;
    return (
        <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-10 gap-2 rounded-lg px-5 text-sm font-semibold"
            onClick={controller.startScan}
            disabled={disabled}

            title={
                controller.isBusy
                    ? "A scan is already in progress for this project"
                    : "Scan cloud resources for copies of these secrets"
            }
            data-testid="button-start-secret-scan"
        >
            {disabled ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />}
            {controller.isStarting ? "Starting..." : controller.isRunning ? "Scanning..." : "Start scan"}
        </Button>
    );
}

function StatTile({
    label,
    value,
    secondary,
    emphasis = false,
}: {
    label: string;
    value: string | number;
    secondary?: string;
    emphasis?: boolean;
}) {
    return (
        <div className="min-w-[104px] rounded-xl border border-border/50 bg-white px-3.5 py-2 shadow-sm dark:bg-card">
            <div className="text-[11px] leading-none text-muted-foreground">{label}</div>
            <div className="mt-1.5 flex items-baseline gap-1">
                <span
                    className={`text-lg font-semibold leading-none ${
                        emphasis ? "text-amber-600 dark:text-amber-400" : "text-foreground"
                    }`}
                >
                    {value}
                </span>
                {secondary ? (
                    <span className="text-xs leading-none text-muted-foreground">{secondary}</span>
                ) : null}
            </div>
        </div>
    );
}

const TARGET_TYPE_LABELS: Record<string, string> = {
    COMPUTE_VM: "Compute Engine VMs",
    STORAGE_BUCKET: "Cloud Storage buckets",
    DATABASE: "Cloud SQL instances",
    KUBERNETES_CLUSTER: "GKE clusters",
};

const TARGET_TYPE_ORDER = ["COMPUTE_VM", "STORAGE_BUCKET", "DATABASE", "KUBERNETES_CLUSTER"];

const INITIAL_CARDS_PER_CATEGORY = 20;

interface TargetCategory {
    type: string;
    label: string;
    targets: SecretDiscoveryScanTarget[];
}

function targetSortWeight(target: SecretDiscoveryScanTarget): number {
    if (target.status === "IN_PROGRESS") return 0;
    if (target.status === "FAILED") return 1;
    if (target.status === "COMPLETED" && (target.findingsCount ?? 0) > 0) return 2;
    if (target.status === "COMPLETED") return 3;
    if (target.status === "PENDING") return 4;
    return 5;
}

function groupTargetsByCategory(targets: SecretDiscoveryScanTarget[]): TargetCategory[] {
    const buckets = new Map<string, SecretDiscoveryScanTarget[]>();
    for (const target of targets) {
        const list = buckets.get(target.targetType);
        if (list) list.push(target);
        else buckets.set(target.targetType, [target]);
    }
    return Array.from(buckets.entries())
        .map(([type, list]) => ({
            type,
            label: TARGET_TYPE_LABELS[type] || type.replace(/_/g, " "),
            targets: [...list].sort(
                (a, b) =>
                    targetSortWeight(a) - targetSortWeight(b) ||
                    (b.findingsCount ?? 0) - (a.findingsCount ?? 0) ||
                    a.resourceName.localeCompare(b.resourceName),
            ),
        }))
        .sort((a, b) => {
            const orderA = TARGET_TYPE_ORDER.indexOf(a.type);
            const orderB = TARGET_TYPE_ORDER.indexOf(b.type);
            return (orderA < 0 ? 99 : orderA) - (orderB < 0 ? 99 : orderB);
        });
}

function TargetCard({ target }: { target: SecretDiscoveryScanTarget }) {
    const style = TARGET_STATE_STYLES[target.status];
    const StatusIcon = style.icon;
    const isRunning = target.status === "IN_PROGRESS";
    const findings = target.findingsCount ?? 0;

    const tooltip = target.failureDetail
        || (target.failureReason && target.failureReason !== "NONE"
            ? target.failureReason.replace(/_/g, " ")
            : `${target.status.replace(/_/g, " ")}${target.zone ? ` · ${target.zone}` : ""}`);

    return (
        <div
            className={`flex items-center gap-2 rounded-lg border bg-white px-3.5 py-2.5 shadow-sm transition-all hover:shadow-md dark:bg-card ${
                isRunning
                    ? "border-primary/40 ring-1 ring-primary/15"
                    : findings > 0
                        ? "border-amber-300/70 ring-1 ring-amber-200/60 dark:border-amber-900/60 dark:ring-amber-900/30"
                        : "border-border/50"
            }`}
            title={tooltip}
            data-testid={`scan-target-${target.resourceName}`}
        >
            <span
                className={`min-w-0 flex-1 truncate text-xs font-medium ${
                    target.status === "SKIPPED" ? "text-muted-foreground" : "text-foreground"
                }`}
            >
                {target.resourceName}
            </span>
            {findings > 0 ? (
                <span className="flex-shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                    {findings}
                </span>
            ) : null}
            <StatusIcon
                className={`h-3.5 w-3.5 flex-shrink-0 ${
                    target.status === "SKIPPED" ? "text-muted-foreground/40" : style.className
                } ${isRunning ? "animate-spin" : ""}`}
            />
        </div>
    );
}

function TargetCategorySection({ category }: { category: TargetCategory }) {
    const [showAll, setShowAll] = useState(false);
    const visible = showAll ? category.targets : category.targets.slice(0, INITIAL_CARDS_PER_CATEGORY);
    const hiddenCount = category.targets.length - visible.length;

    return (
        <div data-testid={`scan-category-${category.type}`}>
            <div className="mb-2.5 flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{category.label}</span>
                <Badge variant="secondary" className="h-5 text-[10px]">{category.targets.length}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {visible.map((target) => (
                    <TargetCard key={target.targetId} target={target} />
                ))}
            </div>
            {hiddenCount > 0 ? (
                <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="mt-2.5 text-xs font-medium text-primary hover:underline"
                    data-testid={`scan-category-show-all-${category.type}`}
                >
                    Show {hiddenCount} more
                </button>
            ) : null}
        </div>
    );
}

export function SecretDiscoveryScanPanel({
    controller,
}: {
    controller: SecretDiscoveryScanController;
}) {
    const { latestScan, isRunning, isBusy, isStarting, startError, targets, areTargetsLoading, targetsEnabled, setTargetsEnabled } =
        controller;

    const finishedTargets = useMemo(
        () =>
            (latestScan?.targetsCompleted ?? 0) +
            (latestScan?.targetsFailed ?? 0) +
            (latestScan?.targetsSkipped ?? 0),
        [latestScan],
    );

    const scanWarning =
        latestScan?.errorMessage && !latestScan.errorMessage.includes("were not searched for")
            ? latestScan.errorMessage
            : null;

    if (!controller.supported) return null;

    if (isStarting && !latestScan) {
        return (
            <Card className="cloud-card-surface rounded-2xl" data-testid="secret-scan-panel-starting">
                <CardContent className="flex items-center gap-3 px-5 py-4">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-foreground">Starting scan...</div>
                        <p className="text-xs text-muted-foreground">Resolving secrets and planning resources.</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!latestScan && !startError) {
        return (
            <Card className="cloud-card-surface rounded-2xl border-dashed" data-testid="secret-scan-panel-empty">
                <CardContent className="flex flex-col items-start gap-1 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <ScanSearch className="h-4 w-4" />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-foreground">Secret footprint scanning</div>
                            <p className="text-xs text-muted-foreground">
                                This project has not been scanned yet. Start a scan to find copies of these secrets
                                across your cloud resources.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const state = isBusy ? "IN_PROGRESS" : latestScan?.status;
    const style = state ? SCAN_STATE_STYLES[state] : null;
    const StateIcon = style?.icon;
    const progress = isBusy ? Math.max(2, latestScan?.progressPercent ?? 0) : 100;

    return (
        <Card className="cloud-card-surface rounded-2xl" data-testid="secret-scan-panel">
            <CardContent className="p-0">
                <div className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <ScanSearch className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold text-foreground">Secret footprint scan</span>
                                {state && style && StateIcon ? (
                                    <span
                                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${style.className}`}
                                        data-testid="secret-scan-status"
                                    >
                                        <StateIcon
                                            className={`h-3 w-3 ${state === "IN_PROGRESS" ? "animate-spin" : ""}`}
                                        />
                                        {SCAN_STATE_LABELS[state]}
                                    </span>
                                ) : null}
                            </div>
                            <p className="mt-1 truncate text-xs text-muted-foreground">
                                {isBusy
                                    ? `Scanning ${finishedTargets} of ${latestScan?.targetsPlanned ?? 0} resources...`
                                    : `Last scan ${formatScanTimestamp(latestScan?.endedAt || latestScan?.startedAt)}`}
                                {latestScan?.initiatedBy ? ` · started by ${latestScan.initiatedBy}` : ""}
                                {!isBusy && latestScan?.durationMillis
                                    ? ` · took ${formatDuration(latestScan.durationMillis)}`
                                    : ""}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5">
                        <StatTile
                            label="Resources scanned"
                            value={latestScan?.targetsCompleted ?? 0}
                            secondary={`of ${latestScan?.targetsPlanned ?? 0}`}
                        />
                        <StatTile label="Secrets searched" value={latestScan?.secretsScanned ?? 0} />
                        <StatTile
                            label="Locations found"
                            value={latestScan?.findingsCount ?? 0}

                            secondary={`across ${latestScan?.secretsScanned ?? 0} secrets`}

                            emphasis={(latestScan?.findingsCount ?? 0) > 0}
                        />
                        {latestScan?.scanId ? (
                            <Button
                                type="button"
                                size="sm"

                                className="h-10 gap-2 rounded-lg px-5 text-sm font-semibold"
                                onClick={() => setTargetsEnabled(!targetsEnabled)}
                                data-testid="button-toggle-scan-targets"
                            >
                                {targetsEnabled ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                {targetsEnabled ? "Hide details" : "View details"}
                            </Button>
                        ) : null}
                    </div>
                </div>

                {isBusy ? (
                    <div className="px-5 pb-4">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                                className="h-full rounded-full bg-primary transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                ) : null}

                {startError ? (
                    <div
                        className="flex items-start gap-2 border-t border-border/50 bg-amber-50/60 px-5 py-3 text-xs text-amber-800 dark:bg-amber-950/20 dark:text-amber-200"
                        data-testid="secret-scan-start-error"
                    >
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                        <span className="flex-1">{startError}</span>
                        <button type="button" className="underline" onClick={controller.clearStartError}>
                            Dismiss
                        </button>
                    </div>
                ) : null}

                {scanWarning ? (
                    <div className="flex items-start gap-2 border-t border-border/50 bg-muted/20 px-5 py-3 text-xs text-muted-foreground">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                        <span>{scanWarning}</span>
                    </div>
                ) : null}

                {targetsEnabled ? (
                    <div className="border-t border-border/50" data-testid="secret-scan-targets">
                        {areTargetsLoading && targets.length === 0 ? (
                            <div className="px-5 py-6 text-center text-xs text-muted-foreground">Loading resources...</div>
                        ) : targets.length === 0 ? (
                            <div className="px-5 py-6 text-center text-xs text-muted-foreground">
                                No resources were planned for this scan.
                            </div>
                        ) : (
                            <div className="space-y-6 px-5 py-5">
                                {groupTargetsByCategory(targets).map((category) => (
                                    <TargetCategorySection key={category.type} category={category} />
                                ))}
                            </div>
                        )}
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}
